// EditIocModal.jsx 🛠️✨ Fully Aligned with AddIocModal + CSS scoped to .edit-ioc-modal
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import TagInput from "./TagInput";
import "./styles/EditIocModal.css";

export default function EditIocModal({ show, onHide, onSuccess, ioc }) {
  const [form, setForm] = useState({
    ioc_id: "",
    ioc_value: "",
    description: "",
    criticality: "1",
    status: "pending",
    related_to_incident: false,
    affected_country: "",
    closing_comment: "",
    tags: [],
    file_attachment: null,
    delete_attachment: false,
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ioc) {
      setForm({
        ioc_id: ioc.id,
        ioc_value: ioc.ioc_value,
        description: ioc.description?.replace(/<\/?[^>]+(>|$)/g, ""),
        criticality: ioc.criticality || "1",
        status: ioc.status || "pending",
        related_to_incident: ioc.related_to_incident == 1,
        affected_country: ioc.affected_country || "",
        closing_comment: ioc.closing_comment || "",
        tags: ioc.tags ? ioc.tags.split(",").map((t) => t.trim()) : [],
        file_attachment: null,
        delete_attachment: false,
      });
    }
  }, [ioc]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

	const handleSubmit = async (e) => {
	  e.preventDefault();
	  setSubmitting(true);

	  try {
		const data = new FormData();
		Object.entries(form).forEach(([key, val]) => {
		  if (key === "tags") {
			data.append("tags", JSON.stringify(val));
		  } else if (key === "file_attachment" && val) {
			data.append("file_attachment", val);
		  } else if (key === "related_to_incident") {
			data.append("related_to_incident", val ? 1 : 0);
		  } else {
			data.append(key, val);
		  }
		});

		const res = await axios.post("/api/edit_ioc.php", data, {
		  headers: { "Content-Type": "multipart/form-data" },
		});

		if (res.data.success) {
		  onSuccess?.();
		  onHide();
		} else {
		  throw new Error(res.data.error || "Update failed.");
		}
	  } catch (err) {
		console.error("Edit IoC error:", err);
	  } finally {
		setSubmitting(false);
	  }
	};

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="edit-ioc-modal"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-pencil-fill me-2 text-warning" /> Edit IoC
            </h5>
            <button className="btn-close btn-close-white" onClick={onHide}></button>
          </div>

          <div className="modal-body">
            {/* IoC Value */}
            <div className="mb-3">
              <label className="form-label">IoC Value</label>
              <input
                className="form-control"
                name="ioc_value"
                value={form.ioc_value}
                onChange={handleChange}
                required
              />
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                maxLength={1000}
                value={form.description}
                onChange={handleChange}
              />
              <small className="text-secondary">Maximum 1000 characters</small>
            </div>

            {/* Criticality & Status */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Criticality</label>
                <select
                  name="criticality"
                  className="form-select"
                  value={form.criticality}
                  onChange={handleChange}
                >
                  <option value="1">Low</option>
                  <option value="2">Medium</option>
                  <option value="3">High</option>
                  <option value="4">Critical</option>
                  <option value="5">Severe</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="pending">Pending</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="closed">Closed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Country */}
            <div className="mb-3">
              <label className="form-label">Affected Country</label>
              <select
                name="affected_country"
                className="form-select"
                value={form.affected_country}
                onChange={handleChange}
              >
                <option value="">Select a country</option>
                <option value="BE">Belgium</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="CH">Switzerland</option>
                <option value="US">United States</option>
                <option value="IT">Italy</option>
                <option value="IE">Ireland</option>
                <option value="ES">Spain</option>
                <option value="GR">Greece</option>
              </select>
            </div>

            {/* Checkbox */}
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="relatedCheckEdit"
                name="related_to_incident"
                checked={form.related_to_incident}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="relatedCheckEdit">
                Related to incident?
              </label>
            </div>

            {/* Closing Comment */}
            {form.status === "closed" && (
              <div className="mb-3">
                <label className="form-label">Closing Comment</label>
                <textarea
                  className="form-control"
                  name="closing_comment"
                  maxLength={500}
                  value={form.closing_comment}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* File Upload */}
            <div className="mb-3">
              <label className="form-label">Replace File (optional)</label>
              <input
                type="file"
                className="form-control"
                accept=".csv,.xls,.pdf"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    file_attachment: e.target.files[0],
                  }))
                }
              />
              {ioc?.file_attachment && (
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="deleteAttachment"
                    name="delete_attachment"
                    checked={form.delete_attachment}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="deleteAttachment">
                    Delete existing file
                  </label>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="mb-3">
              <label className="form-label">Tags</label>
              <TagInput
                tags={form.tags}
                setTags={(tags) =>
                  setForm((prev) => ({ ...prev, tags }))
                }
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-profile" onClick={onHide} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-profile btn-success" disabled={submitting}>
              {submitting ? "Saving..." : "Update"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
